import React, { FC, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Drawer, TransactionProcessing } from '~/components';
import styled from 'styled-components';
import theme from '~/theme';
import { CheckResponse } from '~/api/payrolls';
import SuccessScreen from '~/pages/payrolls/create/SuccessScreen';
import { Consent, Transaction } from '~/types';
import PayrollSteps from '~/pages/payrolls/components/PayrollSteps';
import EnterPaymentDetails from '~/pages/payrolls/create/EnterPaymentDetails';
import { queryClient } from '~/index';
import { Form } from '~/components';
import VerifyBeneficiaryList from '~/pages/payrolls/create/VerifyBeneficiaryList';
import AccountToDebit from '~/pages/payrolls/create/AccountToDebit';
import GuideDrawer from '~/pages/payrolls/components/GuideDrawer';
import useDisclose from '~/hooks/useDisclose';
import layout from '~/hooks/layout';
import { Typography } from 'antd';
import { ApiError } from '~/types/api/ApiError';
import { payrollResourceKey } from '~/pages/payrolls';
import PayrollHeader from '~/pages/payrolls/create/PayrollHeader';

interface Props {
  open: boolean;
  onClose: () => void;
}

const PayrollCreateDrawer: FC<Props> = (props) => {
  const { open, onClose } = props;
  const [t] = useTranslation('payrolls');
  const [form] = Form.useForm();
  const [step, setStep] = useState(0);
  const [fileData, setFileData] = useState<CheckResponse | undefined>();
  const [consent, setConsent] = useState<Consent | undefined>();
  const [consentError, setConsentError] = useState<ApiError | undefined>();
  const [transaction, setTransaction] = useState<Transaction | undefined>();
  const { isOpen: isGuideOpen, onOpen: opedGuide, onClose: closeGuide } = useDisclose(false);
  const isMobile = layout.useIsMobile();

  const goNextStep = () => setStep((prev) => prev + 1);
  const goPreviousStep = () => setStep((prev) => prev - 1);
  const resetStep = () => setStep(0);

  const handleClose = () => {
    queryClient.refetchQueries(payrollResourceKey);
    form.resetFields();
    setFileData(undefined);
    setConsent(undefined);
    setConsentError(undefined);
    setTransaction(undefined);
    resetStep();
    onClose();
  };

  return (
    <>
      <StyledDrawer
        open={open}
        onClose={handleClose}
        title={isMobile ? <PayrollHeader onClick={opedGuide} /> : t('Payroll')}
        placement="bottom"
      >
        <div className="Payroll-MainWrapper">
          {transaction ? (
            <TransactionProcessing
              onComplete={(t) => {
                setTransaction(t);
              }}
              transactionId={transaction?.id}
              completeComponent={
                <SuccessScreen
                  transaction={transaction}
                  payroll={fileData?.data}
                  onClose={handleClose}
                />
              }
            />
          ) : (
            <div className="Payroll-ContentWrapper">
              <div className="Payroll-Content">
                <PayrollSteps currentStep={step} onChange={(current) => setStep(current)} />

                {/* do not use conditional rendering because of problems with file input in EnterPaymentDetails when going previous step */}
                <div className="StepContent" style={{ display: step === 0 ? 'block' : 'none' }}>
                  <EnterPaymentDetails
                    form={form}
                    goNext={(response) => {
                      setFileData(response);
                      goNextStep();
                    }}
                  />
                </div>

                <div className="StepContent">
                  {step === 0 ? null : step === 1 ? (
                    <VerifyBeneficiaryList
                      fileData={fileData}
                      goPrevious={goPreviousStep}
                      goNext={(result) => {
                        setConsent(undefined);
                        setConsentError(undefined);
                        if (result.hasOwnProperty('isAxiosError')) {
                          setConsentError(result as ApiError);
                        } else {
                          setConsent((result as { consent: Consent }).consent);
                        }
                        goNextStep();
                      }}
                    />
                  ) : step === 2 ? (
                    <AccountToDebit
                      data={consent}
                      error={consentError}
                      payroll={fileData?.data}
                      goNext={setTransaction}
                    />
                  ) : null}
                </div>
              </div>
              {!isMobile && (
                <div>
                  <Trans ns="payrolls" i18nKey="payrollGuide">
                    {'Need help? See our '}
                    <Typography.Link onClick={opedGuide}>user guide to payroll</Typography.Link>
                  </Trans>
                </div>
              )}
            </div>
          )}
        </div>
      </StyledDrawer>
      <GuideDrawer open={isGuideOpen} onClose={closeGuide} />
    </>
  );
};

const StyledDrawer = styled(Drawer)`
  .ant-drawer-content,
  .ant-drawer-header {
    background: ${theme.layoutBodyBackground};
  }

  .Payroll-MainWrapper {
    display: flex;
    justify-content: center;
    height: 100%;

    .Payroll-ContentWrapper {
      display: flex;
      flex-direction: column;
      align-items: center;
      max-width: 760px;
      flex-grow: 1;
      justify-content: space-between;
      @media (max-width: ${theme.breakpoints.sm}px) {
        max-width: 100%;
      }
    }

    .Payroll-Content {
      width: 100%;
    }

    .SendButton {
      margin-top: 10px;
      width: 100%;
    }

    .StepContent {
      margin-top: 24px;
      width: 100%;
    }
  }
`;

export default PayrollCreateDrawer;
